import React, { useState, useEffect } from "react";
import { View, Text, StyleSheet, ActivityIndicator } from "react-native";
import { useDispatch, useSelector } from "react-redux";
import TouchPointSurvey, { SurveyItem, SurveyAnswer } from "./TouchPointSurvey";
import {
  fetchTouchPointSurvey,
  ApiSurveyItem,
  fetchProbationSummary,
  ProbationSummaryResponse,
  ProbationTypeData,
} from "../../services/touchPointSurveyService";
import {
  setTouchPointSurveyAnswers,
  setTouchPointSurveyComments,
  setTouchPointSurveyComplete,
  setTouchPointEmployeeSignature,
  setTouchPointManagerSignature,
} from "../../../../store/slices/nhoSlice";
import { useTheme } from "../../../../theme";

interface TouchPointSurveyContainerProps {
  observationType: string;
  testID?: string;
  onAnswersChange?: (
    answers: Record<string, SurveyAnswer>,
    isComplete: boolean
  ) => void;
  onCommentsChange?: (comments: Record<string, string>) => void;
  onSurveyItemsLoaded?: (
    items: Array<{
      id: string;
      sectionValue: string;
      sectionType: string;
      section: number;
    }>
  ) => void;
  onViewOnlyModeChange?: (isViewOnly: boolean) => void;
  onLoadingChange?: (isLoading: boolean) => void;
}

// Utility: filter the full survey item list to only those referenced in the summary
// response, walking each item's parent chain to include the necessary Headers/Sub-Headers.
const filterSurveyItemsBySummary = (
  allItems: SurveyItem[],
  summaryDataIds: Set<string>
): SurveyItem[] => {
  const itemMap = new Map<string, SurveyItem>();
  allItems.forEach((item) => itemMap.set(item.id, item));

  const includedIds = new Set<string>();

  allItems.forEach((item) => {
    if (
      (item.sectionType === "Data" || item.sectionType === "Sub-Data") &&
      summaryDataIds.has(item.id)
    ) {
      includedIds.add(item.id);

      // Walk up the parent chain to include all ancestor Headers / Sub-Headers
      let currentParentId: string | null = item.parentId || null;
      while (currentParentId) {
        if (includedIds.has(currentParentId)) break; // branch already processed
        includedIds.add(currentParentId);
        const parentItem = itemMap.get(currentParentId);
        currentParentId = parentItem?.parentId || null;
      }
    }
  });

  // Return items in their original order so existing sort/render logic is preserved
  return allItems.filter((item) => includedIds.has(item.id));
};

const TouchPointSurveyContainer: React.FC<TouchPointSurveyContainerProps> = ({
  observationType,
  testID = "touchpoint-survey-container",
  onAnswersChange,
  onCommentsChange,
  onSurveyItemsLoaded,
  onViewOnlyModeChange,
  onLoadingChange,
}) => {
  const { colors, resolvedTheme } = useTheme();
  const isDarkTheme = resolvedTheme === "dark";
  // Redux hooks
  const dispatch = useDispatch();
  const { touchPointSurvey, selectedFilters } = useSelector(
    (state: any) => state.nho
  );
  const { selectedLocation } = useSelector((state: any) => state.appData);
  const { currentForm } = touchPointSurvey;

  // State for survey data
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [surveyItems, setSurveyItems] = useState<SurveyItem[]>([]);
  const [isViewOnlyMode, setIsViewOnlyMode] = useState<boolean>(false);

  // Use Redux state for answers and comments, but maintain local state for initialization
  const [answers, setAnswers] = useState<Record<string, SurveyAnswer>>(
    currentForm.surveyAnswers || {}
  );
  const [comments, setComments] = useState<Record<string, string>>(
    currentForm.comments || {}
  );

  // Sync local state with Redux state when it changes (for pre-filling)
  useEffect(() => {
    if (
      currentForm.surveyAnswers &&
      Object.keys(currentForm.surveyAnswers).length > 0
    ) {
      setAnswers(currentForm.surveyAnswers);
    }
    if (currentForm.comments && Object.keys(currentForm.comments).length > 0) {
      setComments(currentForm.comments);
    }
  }, [currentForm.surveyAnswers, currentForm.comments]);

  // Notify parent when loading state changes
  useEffect(() => {
    console.log(
      "TouchPointSurveyContainer: Loading state changed to:",
      loading
    );
    if (onLoadingChange) {
      onLoadingChange(loading);
    }
  }, [loading, onLoadingChange]);

  // Load survey data when component mounts or observationType changes
  useEffect(() => {
    const loadSurveyData = async () => {
      try {
        setLoading(true);
        setError(null);

        console.log(
          "TouchPointSurveyContainer: Fetching survey for observation type:",
          observationType
        );

        // Derive probation state from Redux status
        // new → org context from interceptor, no recordId
        // in-progress → org context from interceptor + recordId
        // completed → skip org context entirely
        const probationStatus = selectedFilters?.status || "";
        const probationState: 'new' | 'in-progress' | 'completed' =
          probationStatus.toLowerCase() === 'completed' ? 'completed' :
          (probationStatus.toLowerCase() === 'in progress' || probationStatus.toLowerCase() === 'in-progress') ? 'in-progress' : 'new';
        const touchpointRecordId = probationState === 'in-progress' ? (selectedFilters?.touchpointId ?? null) : null;

        console.log(
          "TouchPointSurveyContainer: Probation status from Redux:",
          probationStatus,
          "probationState:",
          probationState,
          "touchpointRecordId:",
          touchpointRecordId
        );

        // Get survey items from API
        const apiResponse = await fetchTouchPointSurvey(observationType, probationState, touchpointRecordId);
        console.log(
          "TouchPointSurveyContainer: Raw API response received:",
          apiResponse.length
        );
        console.log(
          "TouchPointSurveyContainer: Sample API items:",
          apiResponse.slice(0, 5)
        );

        // Transform API response to match our SurveyItem interface
        const transformedItems: SurveyItem[] = apiResponse.map(
          (item: ApiSurveyItem) => ({
            id: item.Id.toString(),
            sectionType: item.SectionType as
              | "Header"
              | "Sub-Header"
              | "Data"
              | "Sub-Data"
              | "View",
            sectionValue: item.SectionValue.trim(), // Remove any extra whitespace
            parentId: item.ParentId?.toString() || null,
            sortOrder: item.Sort_Order,
            section: item.Section,
            probation30: item.Probation_30,
            probation60: item.Probation_60,
            probation90: item.Probation_90,
            preferredMethod: item.Preferred_Method,
          })
        );

        console.log(
          "TouchPointSurveyContainer: Transformed items:",
          transformedItems.length
        );
        console.log(
          "TouchPointSurveyContainer: Items by section type:",
          transformedItems.reduce((acc, item) => {
            acc[item.sectionType] = (acc[item.sectionType] || 0) + 1;
            return acc;
          }, {} as Record<string, number>)
        );
        console.log(
          "TouchPointSurveyContainer: Question items (Data/Sub-Data):",
          transformedItems
            .filter(
              (item) =>
                item.sectionType === "Data" || item.sectionType === "Sub-Data"
            )
            .map((item) => ({
              id: item.id,
              sectionValue: item.sectionValue,
              section: item.section,
            }))
        );

        // Sort items to maintain proper hierarchical order:
        // 1. First by Section
        // 2. Then by Sort_Order within each section
        // 3. Then by hierarchy (Headers first, then their children in order)
        const sortedItems = transformedItems.sort((a, b) => {
          // First sort by section
          if (a.section !== b.section) {
            return a.section - b.section;
          }

          // Then by sort order
          if (a.sortOrder !== b.sortOrder) {
            return a.sortOrder - b.sortOrder;
          }

          // Then by hierarchy - maintain parent-child relationships
          const aIsHeader = a.sectionType === "Header";
          const bIsHeader = b.sectionType === "Header";

          if (aIsHeader && !bIsHeader) {
            return -1;
          }
          if (!aIsHeader && bIsHeader) {
            return 1;
          }

          // For items with the same parent, maintain their original order
          if (a.parentId === b.parentId) {
            return parseInt(a.id) - parseInt(b.id);
          }

          return 0;
        });

        console.log(
          "TouchPointSurveyContainer: Loaded and sorted survey items:",
          sortedItems.length
        );
        setSurveyItems(sortedItems);

        // Pass survey items data to parent for API submission
        if (onSurveyItemsLoaded) {
          const surveyItemsForSubmission = sortedItems
            .filter(
              (item) =>
                item.sectionType === "Data" || item.sectionType === "Sub-Data"
            )
            .map((item) => ({
              id: item.id,
              sectionValue: item.sectionValue,
              sectionType: item.sectionType,
              section: item.section, // Include the section ID for API submission
            }));
          onSurveyItemsLoaded(surveyItemsForSubmission);
        }

        // Initialize answers for all questions
        const initialAnswers: Record<string, SurveyAnswer> = {};
        sortedItems.forEach((item: SurveyItem) => {
          // Only initialize answers for question items (Data or Sub-Data)
          if (item.sectionType === "Data" || item.sectionType === "Sub-Data") {
            initialAnswers[item.id] = null; // Default to unanswered
          }
        });

        setAnswers(initialAnswers);

        // Check initial completeness
        const initialCompleteness = checkCompleteness(
          initialAnswers,
          sortedItems
        );
        if (onAnswersChange) {
          onAnswersChange(initialAnswers, initialCompleteness);
        }

        // After loading survey items, try to fetch existing probation data for pre-filling
        await fetchAndPreFillProbationData(sortedItems);
      } catch (err) {
        console.error("Error loading survey data:", err);
        setError("Failed to load survey data. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    // Function to fetch and pre-fill probation data
    const fetchAndPreFillProbationData = async (surveyItems: SurveyItem[]) => {
      try {
        const gpid = selectedFilters?.employeeGpid || selectedFilters?.employee;
        const location = selectedLocation?.displayText;

        if (!gpid || !location) {
          console.log(
            "Missing GPID or location, cannot fetch probation data:",
            { gpid, location }
          );
          return;
        }

        console.log("Attempting to fetch existing probation data for:", {
          gpid,
          location,
          observationType,
        });

        const probationSummary = await fetchProbationSummary(gpid, location);

        if (!probationSummary) {
          console.log("No existing probation data found");
          return;
        }

        console.log("Received probation summary:", probationSummary);

        // Determine the probation type key based on observation type
        const probationTypeKey = getProbationKey(observationType);

        console.log("Looking for probation type:", probationTypeKey);
        console.log(
          "Available probation types:",
          Object.keys(probationSummary)
        );

        const probationTypeData =
          probationSummary[probationTypeKey as keyof ProbationSummaryResponse];

        if (probationTypeData) {
          console.log(`Found existing data for ${probationTypeKey}`);

          // Check the probation status to determine view-only mode
          const probationStatus = probationTypeData.probation.status;
          const isViewOnly =
            probationStatus === "View" || probationStatus === "Completed";

          console.log(
            `Probation status: ${probationStatus}, View-only mode: ${isViewOnly}`
          );

          // Set view-only mode
          setIsViewOnlyMode(isViewOnly);

          // Notify parent component about view-only mode change
          if (onViewOnlyModeChange) {
            onViewOnlyModeChange(isViewOnly);
          }

          // Pre-fill form data
          preFillFormData(probationTypeData as ProbationTypeData, surveyItems);
        } else {
          console.log(`No existing data found for ${probationTypeKey}`);
          // No existing data means editable mode
          setIsViewOnlyMode(false);
          if (onViewOnlyModeChange) {
            onViewOnlyModeChange(false);
          }
        }
      } catch (error) {
        console.log(
          "Failed to fetch probation summary (this is normal if no data exists):",
          error
        );
        // Don't throw error - just continue with empty form
        // Set to editable mode when no data exists
        setIsViewOnlyMode(false);
        if (onViewOnlyModeChange) {
          onViewOnlyModeChange(false);
        }
      }
    };

    // Do not fetch survey data if observationType is empty (e.g. when navigating back
    // to dashboard causes resetTouchPointSurveyForm to clear selectedFilters.observationType)
    if (!observationType) {
      return;
    }

    loadSurveyData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [observationType]);

  // Helper function to get probation key based on observation type
  const getProbationKey = (observationType: string) => {
    if (observationType === "preferred_method") {
      return "Preferred Method";
    } else {
      // For 30, 60, 90 days
      return `Probation ${observationType}`;
    }
  };

  // Function to pre-fill form data from probation summary
  const preFillFormData = (
    probationTypeData: ProbationTypeData,
    surveyItems: SurveyItem[]
  ) => {
    console.log("Pre-filling form with probation data:", probationTypeData);

    // Build the set of Data/Sub-Data IDs present in the summary response
    const summaryDataIds = new Set(
      probationTypeData.data.map((item) => item.Data_Id.toString())
    );

    // Filter the full item list to only those referenced by the summary response,
    // including their parent Headers / Sub-Headers
    const filteredSurveyItems = filterSurveyItemsBySummary(
      surveyItems,
      summaryDataIds
    );

    console.log(
      `TouchPointSurveyContainer: Filtered survey items from ${surveyItems.length} to ${filteredSurveyItems.length} based on summary response`
    );

    // Update the rendered survey items to match only what exists in the summary
    setSurveyItems(filteredSurveyItems);

    // Notify parent with the filtered submission item set
    if (onSurveyItemsLoaded) {
      onSurveyItemsLoaded(
        filteredSurveyItems
          .filter(
            (item) =>
              item.sectionType === "Data" || item.sectionType === "Sub-Data"
          )
          .map((item) => ({
            id: item.id,
            sectionValue: item.sectionValue,
            sectionType: item.sectionType,
            section: item.section,
          }))
      );
    }

    // Debug: Log survey items and their IDs with more detail
    const surveyItemIds = filteredSurveyItems
      .filter(
        (item) => item.sectionType === "Data" || item.sectionType === "Sub-Data"
      )
      .map((item) => ({
        id: item.id,
        sectionValue: item.sectionValue,
        section: item.section,
        sectionType: item.sectionType,
      }));
    console.log("Available survey items for answers:", surveyItemIds);

    // Debug: Show survey items by section
    const surveyBySection = surveyItemIds.reduce((acc, item) => {
      if (!acc[item.section]) {
        acc[item.section] = [];
      }
      acc[item.section].push(item);
      return acc;
    }, {} as Record<number, any[]>);
    console.log("Survey items by section:", surveyBySection);

    // Debug: Log probation data IDs with more detail
    const probationDataIds = probationTypeData.data.map((item) => ({
      Data_Id: item.Data_Id,
      Response: item.Response,
      Section: item.Section,
      ResponseText:
        item.Response === 1
          ? "Yes"
          : item.Response === 2
          ? "No"
          : item.Response === 3
          ? "N/A"
          : "Unknown",
    }));
    console.log("Probation data IDs and responses:", probationDataIds);

    // Debug: Show probation data by section
    const probationBySection = probationDataIds.reduce((acc, item) => {
      if (!acc[item.Section]) {
        acc[item.Section] = [];
      }
      acc[item.Section].push(item);
      return acc;
    }, {} as Record<number, any[]>);
    console.log("Probation data by section:", probationBySection);

    // Debug: Find mismatched IDs
    const surveyItemIdSet = new Set(
      filteredSurveyItems
        .filter(
          (item) =>
            item.sectionType === "Data" || item.sectionType === "Sub-Data"
        )
        .map((item) => item.id)
    );

    const probationDataIdSet = new Set(
      probationTypeData.data.map((item) => item.Data_Id.toString())
    );

    const unmatchedProbationIds = Array.from(probationDataIdSet).filter(
      (id) => !surveyItemIdSet.has(id)
    );
    const unmatchedSurveyIds = Array.from(surveyItemIdSet).filter(
      (id) => !probationDataIdSet.has(id)
    );

    if (unmatchedProbationIds.length > 0) {
      console.warn(
        "Probation data IDs not found in survey items:",
        unmatchedProbationIds
      );
    }

    if (unmatchedSurveyIds.length > 0) {
      console.warn(
        "Survey item IDs not found in probation data:",
        unmatchedSurveyIds
      );
    }

    // Initialize pre-filled answers with current answers as base
    const preFilledAnswers: Record<string, SurveyAnswer> = { ...answers };

    // Map survey answers from probation data
    probationTypeData.data.forEach((item) => {
      // Use Data_Id as the key since our survey items use Id from API as the key
      const itemId = item.Data_Id.toString();

      // Check if this survey item exists in our filtered survey items
      const correspondingSurveyItem = filteredSurveyItems.find(
        (si) => si.id === itemId
      );
      if (
        correspondingSurveyItem &&
        (correspondingSurveyItem.sectionType === "Data" ||
          correspondingSurveyItem.sectionType === "Sub-Data")
      ) {
        // Map Response values based on section
        let answer: SurveyAnswer = null;

        if (observationType === "preferred_method") {
          // For preferred method, always use Yes/No/N/A
          answer =
            item.Response === 1
              ? "Yes"
              : item.Response === 2
              ? "No"
              : item.Response === 3
              ? "N/A"
              : null;
        } else {
          // Map based on section
          switch (correspondingSurveyItem.section) {
            case 1:
              // Section 1: Yes/No/N/A
              answer =
                item.Response === 1
                  ? "Yes"
                  : item.Response === 2
                  ? "No"
                  : item.Response === 3
                  ? "N/A"
                  : null;
              break;
            case 2:
              // Section 2: Yes/No/N/A
              answer =
                item.Response === 1
                  ? "Yes"
                  : item.Response === 2
                  ? "No"
                  : item.Response === 3
                  ? "N/A"
                  : null;
              break;
            case 3:
              // Section 3: Above Target/On Target/Below Target/N/A
              answer =
                item.Response === 1
                  ? "Above Target/On Target"
                  : item.Response === 2
                  ? "Below Target"
                  : item.Response === 3
                  ? "N/A"
                  : null;
              break;
            default:
              // Default to Yes/No/N/A
              answer =
                item.Response === 1
                  ? "Yes"
                  : item.Response === 2
                  ? "No"
                  : item.Response === 3
                  ? "N/A"
                  : null;
              break;
          }
        }

        preFilledAnswers[itemId] = answer;
        console.log(
          `✅ Mapped answer for ID ${itemId} (Section ${correspondingSurveyItem.section}, ${correspondingSurveyItem.sectionValue}): ${answer}`
        );
      } else if (!correspondingSurveyItem) {
        console.warn(
          `❌ Survey item with ID ${itemId} not found in current survey items. Skipping pre-fill for this item.`
        );
      } else {
        console.log(
          `⏭️ Skipping ID ${itemId} - not a question item (${correspondingSurveyItem.sectionType})`
        );
      }
    });

    // Map comments from probation data
    const preFilledComments: Record<string, string> = {};

    console.log("🔍 Processing comment data from probation response:");
    console.log("   observations:", probationTypeData.probation.observations);
    console.log(
      "   additional_observations:",
      probationTypeData.probation.additional_observations
    );

    // Section 1 comments from observations (use dash instead of underscore to match TouchPointSurvey)
    if (probationTypeData.probation.observations) {
      preFilledComments["section-1"] = probationTypeData.probation.observations;
      console.log(
        "✅ Mapped observations to section-1:",
        probationTypeData.probation.observations
      );
    }

    // Section 2 comments from additional_observations
    if (probationTypeData.probation.additional_observations) {
      preFilledComments["section-2"] =
        probationTypeData.probation.additional_observations;
      console.log(
        "✅ Mapped additional_observations to section-2:",
        probationTypeData.probation.additional_observations
      );
    }

    // For "Preferred Method", use a single comment field
    if (
      observationType === "preferred_method" &&
      probationTypeData.probation.observations
    ) {
      preFilledComments["single-comment"] = probationTypeData.probation.observations;
      console.log(
        "✅ Mapped observations to single-comment (preferred method):",
        probationTypeData.probation.observations
      );
    }

    console.log("Final pre-filled answers:", preFilledAnswers);
    console.log("Pre-filled comments:", preFilledComments);

    // Debug: Count how many answers were successfully mapped
    const totalQuestions = filteredSurveyItems.filter(
      (item) => item.sectionType === "Data" || item.sectionType === "Sub-Data"
    ).length;
    const mappedAnswers = Object.values(preFilledAnswers).filter(
      (answer) => answer !== null
    ).length;
    const totalProbationResponses = probationTypeData.data.length;

    console.log("📊 Pre-fill Summary:");
    console.log(`   Total survey questions: ${totalQuestions}`);
    console.log(`   Total probation responses: ${totalProbationResponses}`);
    console.log(`   Successfully mapped answers: ${mappedAnswers}`);
    console.log(`   Missing answers: ${totalQuestions - mappedAnswers}`);

    // Show which IDs have answers vs which don't
    const answeredIds = Object.entries(preFilledAnswers)
      .filter(([_, answer]) => answer !== null)
      .map(([id, _]) => id);
    const unansweredIds = Object.entries(preFilledAnswers)
      .filter(([_, answer]) => answer === null)
      .map(([id, _]) => id);

    if (answeredIds.length > 0) {
      console.log(`✅ Answered question IDs: ${answeredIds.join(", ")}`);
    }
    if (unansweredIds.length > 0) {
      console.log(`❌ Unanswered question IDs: ${unansweredIds.join(", ")}`);
    }

    // Update local state
    setAnswers(preFilledAnswers);
    setComments(preFilledComments);

    // Update Redux state
    dispatch(setTouchPointSurveyAnswers(preFilledAnswers));
    dispatch(setTouchPointSurveyComments(preFilledComments));

    // Handle signature pre-filling
    if (probationTypeData.probation.employee_signature) {
      const employeeSignature = {
        fileName:
          probationTypeData.probation.employee_signature.split("/").pop() ||
          null,
        imageUrl: probationTypeData.probation.employee_signature,
      };
      dispatch(setTouchPointEmployeeSignature(employeeSignature));
      console.log("Pre-filled employee signature:", employeeSignature);
    }

    if (probationTypeData.probation.manager_signature) {
      const managerSignature = {
        fileName:
          probationTypeData.probation.manager_signature.split("/").pop() ||
          null,
        imageUrl: probationTypeData.probation.manager_signature,
      };
      dispatch(setTouchPointManagerSignature(managerSignature));
      console.log("Pre-filled manager signature:", managerSignature);
    }

    // Check if all questions are answered after pre-filling
    const isComplete = checkCompleteness(preFilledAnswers, filteredSurveyItems);
    dispatch(setTouchPointSurveyComplete(isComplete));

    // Notify parent components
    if (onAnswersChange) {
      onAnswersChange(preFilledAnswers, isComplete);
    }

    if (onCommentsChange) {
      onCommentsChange(preFilledComments);
    }

    console.log("Form pre-filled successfully. Complete:", isComplete);
  };

  // Function to check if all questions are answered
  const checkCompleteness = (
    currentAnswers: Record<string, SurveyAnswer>,
    questionItems: SurveyItem[]
  ) => {
    // Get all question items (Data and Sub-Data types)
    const questionIds = questionItems
      .filter(
        (item) => item.sectionType === "Data" || item.sectionType === "Sub-Data"
      )
      .map((item) => item.id);

    // Check if all questions have non-null answers
    const allAnswered = questionIds.every((id) => currentAnswers[id] != null);

    console.log("Question IDs:", questionIds);
    console.log("Current answers:", currentAnswers);
    console.log("All answered:", allAnswered);

    return allAnswered;
  };

  // Handle answer changes
  const handleAnswerChange = (newAnswers: Record<string, SurveyAnswer>) => {
    setAnswers(newAnswers);

    // Dispatch to Redux
    dispatch(setTouchPointSurveyAnswers(newAnswers));

    // Check if all questions are answered
    const isComplete = checkCompleteness(newAnswers, surveyItems);
    dispatch(setTouchPointSurveyComplete(isComplete));

    // Call parent callback if provided
    if (onAnswersChange) {
      onAnswersChange(newAnswers, isComplete);
    }

    console.log("Answers updated:", newAnswers, "Complete:", isComplete);
  };

  // Handle comment changes
  const handleCommentChange = (newComments: Record<string, string>) => {
    setComments(newComments);

    // Dispatch to Redux
    dispatch(setTouchPointSurveyComments(newComments));

    // Call parent callback if provided
    if (onCommentsChange) {
      onCommentsChange(newComments);
    }

    console.log("Comments updated:", newComments);
  };

  // Show loading state
  if (loading) {
    return (
      <View
        style={[
          styles.centerContainer,
          isDarkTheme
            ? { backgroundColor: colors.background.surface }
            : { backgroundColor: "#fff" },
        ]}
        testID="loading-state"
      >
        <ActivityIndicator size="large" color={colors.brand.primary} />
        <Text
          style={[
            styles.loadingText,
            isDarkTheme
              ? { color: colors.text.primary }
              : { color: colors.text.secondary },
          ]}
        >
          Loading survey...
        </Text>
      </View>
    );
  }

  // Show error state
  if (error) {
    return (
      <View
        style={[
          styles.centerContainer,
          isDarkTheme
            ? { backgroundColor: colors.background.surface }
            : { backgroundColor: "#fff" },
        ]}
        testID="error-state"
      >
        <Text style={styles.errorText}>{error}</Text>
      </View>
    );
  }

  // Show empty state
  if (surveyItems.length === 0) {
    return (
      <View
        style={[
          styles.centerContainer,
          isDarkTheme
            ? { backgroundColor: colors.background.surface }
            : { backgroundColor: "#fff" },
        ]}
        testID="empty-state"
      >
        <Text
          style={[
            styles.emptyText,
            isDarkTheme
              ? { color: colors.text.primary }
              : { color: colors.text.secondary },
          ]}
        >
          No survey questions available for this touchpoint type.
        </Text>
      </View>
    );
  }

  // Render the survey
  return (
    <TouchPointSurvey
      items={surveyItems}
      observationType={observationType}
      initialAnswers={answers}
      initialComments={comments}
      onAnswerChange={handleAnswerChange}
      onCommentChange={handleCommentChange}
      isViewOnlyMode={isViewOnlyMode}
      testID={testID}
    />
  );
};

const styles = StyleSheet.create({
  centerContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    padding: 20,
  },
  loadingText: {
    fontSize: 16,
    color: "#3A3A3A",
    marginTop: 10,
  },
  errorText: {
    fontSize: 16,
    color: "#D32F2F",
    textAlign: "center",
  },
  emptyText: {
    fontSize: 16,
    color: "#6C757D",
    textAlign: "center",
  },
});

export default TouchPointSurveyContainer;
